import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { LoadingSkeleton } from '@/components/ui/loading-skeleton'
import { ConfidenceDetail } from '@/components/review-chase/confidence-detail'
import {
  useReviewConfidence,
  getConfidenceTier,
  CONFIDENCE_TIER_LABEL,
  type ConfidenceTier,
} from '@/hooks/use-review-confidence'

interface ConfidenceCardProps {
  reservationId: string
}

const BAR_TIER_CLASSES: Record<ConfidenceTier, string> = {
  high: 'bg-mint',
  mid: 'bg-amber',
  low: 'bg-danger',
}

const LABEL_TIER_CLASSES: Record<ConfidenceTier, string> = {
  high: 'text-mint',
  mid: 'text-amber',
  low: 'text-danger',
}

/**
 * The precomputed five-star confidence score (CAP-301), read here rather
 * than generated - see review-chase/confidence-toggle.tsx's identical note.
 * Renders nothing when there's no score yet (a non-Airbnb reservation, or
 * one whose review window hasn't opened) rather than an empty card with
 * nothing in it, matching SecurityDepositCard's own "not applicable ->
 * render null" convention below.
 */
export function ConfidenceCard({ reservationId }: ConfidenceCardProps) {
  const { confidence, recommendation, propertyPrior, isLoading, isQueued } =
    useReviewConfidence(reservationId)

  if (isLoading && !confidence) return <LoadingSkeleton className="h-32 w-full" />
  if (isQueued) return <LoadingSkeleton className="h-32 w-full" />
  if (!confidence || confidence.status !== 'completed' || confidence.score == null) return null

  const tier = getConfidenceTier(confidence.score)

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <CardTitle>Five-star confidence</CardTitle>
        <span className={`text-sm font-semibold tabular-nums ${LABEL_TIER_CLASSES[tier]}`}>
          {confidence.score}/100
        </span>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/10">
          <div
            className={`h-full rounded-full ${BAR_TIER_CLASSES[tier]}`}
            style={{ width: `${confidence.score}%` }}
          />
        </div>
        <div>
          <div className={`text-sm font-semibold ${LABEL_TIER_CLASSES[tier]}`}>
            {CONFIDENCE_TIER_LABEL[tier]}
          </div>
          <p className="mt-0.5 text-xs text-textMuted">
            Computed from the guest message history when the review window opened.
          </p>
        </div>
        <ConfidenceDetail
          confidence={confidence}
          tier={tier}
          summaryFallback={false}
          recommendation={recommendation}
          propertyPrior={propertyPrior}
        />
      </CardContent>
    </Card>
  )
}
